import "@/App.css";
import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PaywallSheet } from "@/components/PaywallSheet";
import { CommandPalette } from "@/components/a11y/CommandPalette";
import { AccessibilityPanel, SkipLink } from "@/components/a11y/AccessibilityPanel";
import AccessibilityPage from "@/pages/site/AccessibilityPage";
import AdminApp, { ADMIN_PATH, ADMIN_HOST_MODE } from "@/pages/admin/AdminApp";
import { NotFound, ScrollManager } from "@/components/SiteLinks";
import Marketplace from "@/pages/Marketplace";
import ListingDetail from "@/pages/ListingDetail";
import Bookings from "@/pages/Bookings";
import Profile from "@/pages/Profile";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import { WorldsPage, WorldPage, StagePage, YasminPage } from "@/pages/site/Discover";
import { TravelPage, EsimsPage, CityPage } from "@/pages/site/Travel";
import { BusinessPage, PricingPage } from "@/pages/site/Business";
import { AboutPage, PaymentsPage, CareersPage, ContactPage, GetTheAppPage } from "@/pages/site/Company";
import { BlogPage, BlogPostPage, LegalDocPage, AffiliatePage, CookiesPage } from "@/pages/site/Content";
import { PartnerShell } from "@/components/partner/PartnerShell";
import { PartnerInsights, PartnerClients, PartnerMarketing, PartnerOffers, PartnerProducts } from "@/pages/partner/PartnerTools";
import { PartnerCalendar, PartnerTeam, PartnerRota, PartnerQuickReplies } from "@/pages/partner/PartnerOps";
import { PartnerInvoices, PartnerRefunds, PartnerHours, PartnerPackages } from "@/pages/partner/PartnerMoney";
import { PartnerAssistant, PartnerForms, PartnerKitchen } from "@/pages/partner/PartnerAssist";
import { PartnerPosts, PartnerAds, PartnerBoosts, PartnerTrust, PartnerManualBooking, PartnerPayouts } from "@/pages/partner/PartnerGrow";
import BookPage from "@/pages/site/BookPage";
import PartnerDeveloper from "@/pages/partner/Developer";
import StageFeed from "@/pages/stage/StageFeed";
import Creators from "@/pages/stage/Creators";
import CreatorProfile from "@/pages/stage/CreatorProfile";
import PostDetail from "@/pages/stage/PostDetail";
import TripPlanner from "@/pages/travel/TripPlanner";
import { MyTrips, TripDetail } from "@/pages/travel/MyTrips";
import YasminPlan from "@/pages/travel/YasminPlan";
import { DISCOVER_ROUTES } from "@/routes/discover.routes";
import { ACCOUNT_ROUTES } from "@/routes/account.routes";

// The home page carries the GSAP/Lenis motion layer; it loads on demand so app screens stay light.
const Landing = React.lazy(() => import("@/pages/Landing"));
const OpsApp = React.lazy(() => import("@/ops/OpsApp"));
import PartnerStart from "@/pages/partner/PartnerStart";
import { PartnerOverview, PartnerListings, PartnerListingEditor, PartnerBookings, PartnerEarnings, PartnerPlan, BillingReturn, ConnectReturn } from "@/pages/partner/PartnerPages";
import PartnerUsage from "@/pages/partner/PartnerUsage";
import PartnerAccounting from "@/pages/partner/PartnerAccounting";
import PartnerSwitch from "@/pages/partner/PartnerSwitch";
import { PartnerTargets, PartnerMemberships, PartnerImport, PartnerListingHelper, PartnerFood, PartnerSafety, PartnerStudio, PartnerChats, PartnerGrowth } from "@/pages/partner/PartnerMore";

// React Router can't express "/@handle" as a pattern, so the catch-all resolves it.
function CatchAll() {
  const { pathname } = useLocation();
  const m = /^\/@([a-z0-9_]{3,20})\/?$/i.exec(pathname);
  return m ? <CreatorProfile key={m[1].toLowerCase()} handle={m[1]} /> : <NotFound />;
}

// The assistant was renamed from Hanna to Yasmin: old links keep working (query kept for Stripe returns).
function LegacyRedirect({ to }) {
  const loc = useLocation();
  return <Navigate to={to + loc.search + loc.hash} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
      <SkipLink />
        <ScrollManager />
        <AuthProvider>
          {ADMIN_HOST_MODE ? <Routes><Route path="/*" element={<AdminApp base="" />} /></Routes> : <Routes>
          <Route path={ADMIN_PATH + "/*"} element={<AdminApp />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/book/:providerId" element={<BookPage />} />
            <Route element={<Layout />}>
              <Route path="/" element={<Suspense fallback={<div className="min-h-[60vh] bg-[#FBF7F1]" />}><Landing /></Suspense>} />
              <Route path="/marketplace" element={<Marketplace />} />
              <Route path="/listing/:id" element={<ListingDetail />} />
              <Route
                path="/bookings"
                element={
                  <ProtectedRoute>
                    <Bookings />
                  </ProtectedRoute>
                }
              />
              <Route path="/bookings/:id" element={<ProtectedRoute><Bookings /></ProtectedRoute>} />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              {/* Partner hub (provider side) */}
              <Route path="/partner/start" element={<ProtectedRoute><PartnerStart /></ProtectedRoute>} />
              <Route path="/billing-return" element={<ProtectedRoute><BillingReturn /></ProtectedRoute>} />
              <Route path="/connect-return" element={<ProtectedRoute><ConnectReturn /></ProtectedRoute>} />
              <Route path="/partner" element={<ProtectedRoute><PartnerShell /></ProtectedRoute>}>
                <Route index element={<PartnerOverview />} />
                <Route path="listings" element={<PartnerListings />} />
                <Route path="listings/:id" element={<PartnerListingEditor />} />
                <Route path="bookings" element={<PartnerBookings />} />
                <Route path="earnings" element={<PartnerEarnings />} />
                <Route path="plan" element={<PartnerPlan />} />
                <Route path="usage" element={<PartnerUsage />} />
                <Route path="accounting" element={<PartnerAccounting />} />
                <Route path="switch" element={<PartnerSwitch />} />
          <Route path="insights" element={<PartnerInsights />} />
          <Route path="calendar" element={<PartnerCalendar />} />
          <Route path="team" element={<PartnerTeam />} />
          <Route path="rota" element={<PartnerRota />} />
          <Route path="replies" element={<PartnerQuickReplies />} />
          <Route path="hours" element={<PartnerHours />} />
          <Route path="invoices" element={<PartnerInvoices />} />
          <Route path="refunds" element={<PartnerRefunds />} />
          <Route path="packages" element={<PartnerPackages />} />
          <Route path="assistant" element={<PartnerAssistant />} />
          <Route path="forms" element={<PartnerForms />} />
          <Route path="kitchen" element={<PartnerKitchen />} />
          <Route path="posts" element={<PartnerPosts />} />
          <Route path="ads" element={<PartnerAds />} />
          <Route path="boost" element={<PartnerBoosts />} />
          <Route path="trust" element={<PartnerTrust />} />
          <Route path="new-booking" element={<PartnerManualBooking />} />
          <Route path="payouts" element={<PartnerPayouts />} />
          <Route path="developer" element={<PartnerDeveloper />} />
          <Route path="clients" element={<PartnerClients />} />
          <Route path="marketing" element={<PartnerMarketing />} />
          <Route path="offers" element={<PartnerOffers />} />
          <Route path="products" element={<PartnerProducts />} />
          <Route path="targets" element={<PartnerTargets />} />
          <Route path="memberships" element={<PartnerMemberships />} />
          <Route path="import" element={<PartnerImport />} />
          <Route path="listing-helper" element={<PartnerListingHelper />} />
          <Route path="food" element={<PartnerFood />} />
          <Route path="safety" element={<PartnerSafety />} />
          <Route path="studio" element={<PartnerStudio />} />
          <Route path="chats" element={<PartnerChats />} />
          <Route path="growth" element={<PartnerGrowth />} />
              </Route>
              {/* Information pages (the copy from the old fableplus.uk site, same addresses) */}
              <Route path="/worlds" element={<WorldsPage />} />
              <Route path="/worlds/:key" element={<WorldPage />} />
              <Route path="/stage" element={<StagePage />} />
              <Route path="/stage/feed" element={<StageFeed />} />
              <Route path="/stage/post/:id" element={<PostDetail />} />
              <Route path="/creators" element={<Creators />} />
              <Route path="/creators/:handle" element={<CreatorProfile />} />
              <Route path="/hanna" element={<LegacyRedirect to="/yasmin" />} />
              <Route path="/hanna/chat" element={<LegacyRedirect to="/yasmin/chat" />} />
              <Route path="/plus/hanna" element={<LegacyRedirect to="/plus/yasmin" />} />
              <Route path="/agents/hanna" element={<LegacyRedirect to="/agents/yasmin" />} />
        <Route path="/yasmin" element={<YasminPage />} />
              <Route path="/travel" element={<TravelPage />} />
              <Route path="/travel/plan" element={<TripPlanner />} />
              <Route path="/travel/trips" element={<ProtectedRoute><MyTrips /></ProtectedRoute>} />
              <Route path="/travel/trips/:id" element={<ProtectedRoute><TripDetail /></ProtectedRoute>} />
              <Route path="/travel/plans/:id" element={<ProtectedRoute><YasminPlan /></ProtectedRoute>} />
              <Route path="/travel/esims" element={<EsimsPage />} />
              <Route path="/travel/:slug" element={<CityPage />} />
              <Route path="/business" element={<BusinessPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/payments" element={<PaymentsPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/accessibility" element={<AccessibilityPage />} />
              <Route path="/get-the-app" element={<GetTheAppPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/legal/terms" element={<LegalDocPage doc="terms" />} />
              <Route path="/legal/privacy" element={<LegalDocPage doc="privacy" />} />
              <Route path="/legal/affiliate-disclosure" element={<AffiliatePage />} />
              <Route path="/legal/cookies" element={<CookiesPage />} />
              {[...DISCOVER_ROUTES, ...ACCOUNT_ROUTES].map((r) => (
                <Route key={r.path} path={r.path} element={r.protected ? <ProtectedRoute>{r.element}</ProtectedRoute> : r.element} />
              ))}
              <Route path="/ops/*" element={<Suspense fallback={<div className="min-h-screen" />}><OpsApp /></Suspense>} />
              <Route path="*" element={<CatchAll />} />
            </Route>
          </Routes>}
          <Toaster position="top-center" richColors />
        <PaywallSheet />
          {!ADMIN_HOST_MODE ? <><CommandPalette /><AccessibilityPanel /></> : null}
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
